import { Fragment, memo, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import SubtitlesOffIcon from '@mui/icons-material/SubtitlesOff';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CreditCardOffIcon from '@mui/icons-material/CreditCardOff';
import type { Project, Session } from '@/api/client';
import { api } from '@/api/client';
import { getBaseStatus } from '@/utils/projectStatuses';
import { groupProjectsForPickerSections, groupReadersForPickerSections } from '@/utils/pickerProgressGroups';
import { getProjectTypeBadgeColor } from '@/styles/projectColorTokens';
import { parseTimeToMinutes, buildTimeFromMinutes, SESSION_DRAG_TYPE } from '@/schedule/scheduleUtils';
import { SESSION_CHAT_ATTACH_TYPE } from '@/constants/chatDragTypes';
import { getSessionDropEffect, hasCopyModifier } from '@/schedule/scheduleModifierKeys';
import { SESSION_NO_PROJECT_LABEL, SESSION_READER_NOT_SET_LABEL } from '@/constants/sessionReader';
import {
  useChatAttachDragSetter,
  getTransparentDragImage,
  CHAT_ATTACH_DRAG_POSITION_MODE,
  CHAT_ATTACH_DRAG_OFFSET_Y_PX,
} from '@/contexts/ChatAttachDragContext';
import type { ScheduleSearchFilterState } from '@/schedule/scheduleSearchFilter';
import { highlightPlainText, SEARCH_HIGHLIGHT_CLASS_SCHEDULE } from '@/utils/highlightSearchTerms';
import {
  scheduleDragState,
  setDraggingSession,
  clearDraggingSession,
  consumeSessionDropCommitted,
} from '@/schedule/scheduleDragState';
import { TOTAL_MINUTES, CONTENT_HEIGHT } from '@/schedule/scheduleConstants';
import { useScheduleHandlers } from '@/contexts/ScheduleContext';
import { useTeleportOptional } from '@/contexts/TeleportContext';
import { ContextMenu } from '@/components/ui/ContextMenu/ContextMenu';
import { ContextMenuSectionHeader } from '@/components/ui/ContextMenu/ContextMenuSectionHeader';
import { ContextMenuSubmenu } from '@/components/ui/ContextMenu/ContextMenuSubmenu';
import { Badge } from '@/components/ui/Badge/Badge';
import { DeadlineRemainingBadge } from '@/components/ui/DeadlineRemainingBadge/DeadlineRemainingBadge';
import { ProjectNameWithTypeGlyph } from '@/components/ui/ProjectTypeGlyph/ProjectTypeGlyph';
import {
  DeleteIcon,
  EditIcon,
  ArrowForwardIcon,
  ChevronRightIcon,
  HeadsetMicIcon,
  TextDecreaseIcon,
  VisibilityIcon,
  VisibilityOffIcon,
  PersonIcon,
  PersonOffIcon,
  TuneIcon,
} from '@/components/ui/Icons';
import { SessionCancelReasonModal } from '@/components/SessionDialog/SessionCancelReasonModal';
import { SessionBubbleContent } from './SessionBubbleContent';
import { FontSizeSelector } from './FontSizeSelector';
import { getContentFontSize } from './fontSizeUtils';
import '@/components/ui/overlayPickerBadges.css';
import { SessionCommentShell } from '@/features/project-cell-comments/SessionCommentShell';
import { useCellCommentShellOptional } from '@/features/project-cell-comments/cellCommentShellContext';
import { CellCommentCornerBadge } from '@/features/project-cell-comments/CellCommentCornerBadge';
import { HistoryIcon } from '@/components/ui/Icons';
import { SessionChangeHistoryPopover } from '@/features/session-change-history/SessionChangeHistoryPopover';
import { setLastUndoRedoSessionId } from '@/features/session-change-history/sessionUndoRedoStore';
import './SessionBubble.css';

const SUBMENU_CLOSE_DELAY_MS = 150;
const DEFAULT_ENGINEER_COLOR = '#9e9e9e';
const SESSION_BUBBLE_PROJECT_PICK_LIMIT = 500;

function getSessionReaderNames(session: Session): string {
  return session.readers?.length ? session.readers.map((r) => r.name).join(', ') : (session.reader_name?.trim() || '');
}

function getPrimarySessionReaderId(session: Session): string | null {
  return session.readers?.[0]?.id ?? session.reader_ids?.[0] ?? session.reader_id ?? null;
}

function afterNextPaint(callback: () => void): void {
  requestAnimationFrame(() => requestAnimationFrame(callback));
}

const SCHEDULE_SEARCH_INACTIVE: ScheduleSearchFilterState = {
  active: false,
  words: [],
  matchIds: null,
};

interface SessionBubbleProps {
  session: Session;
  style: { top: number; left: number; width: number; height: number; visualInset?: number };
  /** Число комментариев в треде сессии (сводка) */
  commentCount?: number;
  scheduleSearchFilter?: ScheduleSearchFilterState;
}

function SessionBubbleRoot({
  session,
  style,
  commentCount = 0,
  scheduleSearchFilter = SCHEDULE_SEARCH_INACTIVE,
}: SessionBubbleProps) {
  const startStr = (session.start as string).slice(0, 5);
  const endStr = (session.end as string).slice(0, 5);
  const timeRange = `${startStr} - ${endStr}`;
  const isNote = session.session_type === 'заметка';
  const isMontage = session.session_type === 'монтаж';
  const projectName = session.project_id
    ? (session.project_name || session.project_id || 'Проект удалён')
    : SESSION_NO_PROJECT_LABEL;
  const header = isNote
    ? (session.title || 'Заметка')
    : isMontage
      ? ''
      : (getSessionReaderNames(session) || SESSION_READER_NOT_SET_LABEL);
  const sessionThreadTitle = useMemo(() => {
    if (isNote) return `${timeRange} — ${session.title || 'Заметка'}`;
    if (isMontage) return `${timeRange} — Монтаж · ${projectName}`;
    return `${timeRange} — ${header} · ${projectName}`;
  }, [timeRange, isNote, isMontage, projectName, header, session.title]);

  const isMicroSession = useMemo(() => {
    const a = parseTimeToMinutes(session.start as string);
    const b = parseTimeToMinutes(session.end as string);
    const d = b - a;
    return d > 0 && d < 60;
  }, [session.start, session.end]);
  const [isDragging, setIsDragging] = useState(false);

  const positionStyle = useMemo(
    () => {
      const h = Math.max(style.height, 20);
      return {
        top: style.top,
        left: style.left,
        width: style.width,
        height: h,
        ['--session-shell-h' as string]: `${h}px`,
        ['--session-visual-inset' as string]: `${style.visualInset ?? 0}px`,
      } as React.CSSProperties;
    },
    [style.top, style.left, style.width, style.height, style.visualInset]
  );
  const shellClassName = [
    isMicroSession ? 'session-shell--micro' : '',
    isDragging ? 'session-shell--dragging' : '',
    scheduleSearchFilter.active && !scheduleSearchFilter.matchIds?.has(session.id)
      ? 'project-table-cell-comment-shell--schedule-search-dim'
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const searchHighlightWords =
    scheduleSearchFilter.active && scheduleSearchFilter.matchIds?.has(session.id)
      ? [...scheduleSearchFilter.words]
      : [];

  return (
    <SessionCommentShell
      sessionId={session.id}
      title={sessionThreadTitle}
      commentCount={commentCount}
      positionStyle={positionStyle}
      hideWrapperContextMenu
      className={shellClassName || undefined}
    >
      <SessionBubbleCore
        session={session}
        onDraggingChange={setIsDragging}
        searchHighlightWords={searchHighlightWords}
      />
    </SessionCommentShell>
  );
}

/** Строит CSS-стили для тинта цвета звукорежиссёра */
function buildEngineerStyle(hex: string): React.CSSProperties {
  const clean = hex.replace(/^#/, '');
  const len = clean.length;
  const r = len === 3 ? parseInt(clean[0] + clean[0], 16) : parseInt(clean.slice(0, 2), 16);
  const g = len === 3 ? parseInt(clean[1] + clean[1], 16) : parseInt(clean.slice(2, 4), 16);
  const b = len === 3 ? parseInt(clean[2] + clean[2], 16) : parseInt(clean.slice(4, 6), 16);
  const overlay = `linear-gradient(rgba(${r},${g},${b},0.6), rgba(${r},${g},${b},0.6))`;
  const tintedShadow = `0 2px 6px rgba(${r},${g},${b},0.45)`;
  return {
    background: `${overlay}, var(--surface, #fff)`,
    borderColor: 'rgba(0, 0, 0, 0.05)',
    boxShadow: tintedShadow,
    '--session-engineer-border-hover': 'rgba(0, 0, 0, 0.08)',
    '--session-engineer-shadow-hover': `0 4px 12px rgba(${r},${g},${b},0.55)`,
    /* .session-bubble_excess: серый border/box-shadow с !important перебивает инлайн — подставляем те же бордер и тинт-тень, что у обычной сессии с этим звукорежиссёром */
    '--session-engineer-tinted-shadow': tintedShadow,
  } as React.CSSProperties;
}

function SessionBubbleCore({
  session,
  onDraggingChange,
  searchHighlightWords = [],
}: {
  session: Session;
  onDraggingChange?: (isDragging: boolean) => void;
  searchHighlightWords?: string[];
}) {
  const cellCommentShell = useCellCommentShellOptional();
  const {
    onSessionEdit,
    onSessionClick,
    onSessionDelete,
    onSessionResize,
    onSessionDropOnSession,
    onSessionUpdate,
    onSessionOptimisticUpdate,
    onRestoreCanceledSession,
    soundengineers,
  } = useScheduleHandlers();
  const teleport = useTeleportOptional();

  const [resizeTimePreview, setResizeTimePreview] = useState<{ start: number; end: number } | null>(null);
  const startMinsForUi = resizeTimePreview?.start ?? parseTimeToMinutes(session.start as string);
  const endMinsForUi = resizeTimePreview?.end ?? parseTimeToMinutes(session.end as string);
  const startStr = buildTimeFromMinutes(startMinsForUi);
  const endStr = buildTimeFromMinutes(endMinsForUi);
  const tooltipTimeSpan = `${startStr} — ${endStr}`;
  const isNote = session.session_type === 'заметка';
  const isMontage = session.session_type === 'монтаж';
  const isPodcastSession = session.session_type === 'запись' && session.session_subtype === 'podcast';
  const projectName = session.project_id
    ? (session.project_name || session.project_id || 'Проект удалён')
    : SESSION_NO_PROJECT_LABEL;
  const readerNames = getSessionReaderNames(session);
  const headerReaderMuted = !isNote && !isMontage && !isPodcastSession && !readerNames;
  const header = isNote
    ? (session.title || 'Заметка')
    : isMontage
      ? ''
      : isPodcastSession
        ? projectName
        : (readerNames || SESSION_READER_NOT_SET_LABEL);
  const subtitle = isNote
    ? (session.content ?? '')
    : isPodcastSession
      ? (readerNames || SESSION_READER_NOT_SET_LABEL)
      : projectName;
  const schHl = searchHighlightWords;
  const hlSch = (s: string) =>
    schHl.length ? highlightPlainText(s, schHl, SEARCH_HIGHLIGHT_CLASS_SCHEDULE) : s;
  const withProjectTypeGlyph = (name: ReactNode) => (
    <ProjectNameWithTypeGlyph type={session.project_type}>{name}</ProjectNameWithTypeGlyph>
  );
  let headerDisplay: ReactNode =
    schHl.length && typeof header === 'string' && header
      ? highlightPlainText(header, schHl, SEARCH_HIGHLIGHT_CLASS_SCHEDULE)
      : header;
  let subtitleDisplay: ReactNode =
    schHl.length && typeof subtitle === 'string' && subtitle
      ? highlightPlainText(subtitle, schHl, SEARCH_HIGHLIGHT_CLASS_SCHEDULE)
      : subtitle;
  let excessProjectDisplay: ReactNode =
    schHl.length && typeof projectName === 'string' && projectName
      ? highlightPlainText(projectName, schHl, SEARCH_HIGHLIGHT_CLASS_SCHEDULE)
      : projectName;
  if (!isNote) {
    if (isPodcastSession) {
      headerDisplay = withProjectTypeGlyph(headerDisplay);
    } else {
      subtitleDisplay = withProjectTypeGlyph(subtitleDisplay);
    }
    excessProjectDisplay = withProjectTypeGlyph(excessProjectDisplay);
  }
  const headerOrDash = header ? headerDisplay : schHl.length ? hlSch('—') : '—';
  /** В конец тултипа: прогресс чтеца по книге или, если чтеца нет, прогресс проекта (например монтаж). */
  const tooltipProgressStr = (() => {
    if (isNote) return null;
    if (getPrimarySessionReaderId(session)) {
      return `Прогресс: ${Math.min(100, Math.max(0, session.reader_progress ?? 0))}%`;
    }
    if (
      session.project_id &&
      session.project_progress != null &&
      !Number.isNaN(Number(session.project_progress))
    ) {
      return `Прогресс: ${Math.min(100, Math.max(0, Number(session.project_progress)))}%`;
    }
    return null;
  })();
  const isCanceled = session.is_canceled ?? false;
  const cancelTooltip = isCanceled ? 'Отменена' : null;
  const tooltipTitle = isNote
    ? [session.title || 'Заметка', (session.content ?? '').trim() || null, tooltipTimeSpan, cancelTooltip]
        .filter(Boolean)
        .join(' • ')
    : [header.trim() || null, typeof subtitle === 'string' && subtitle.trim() ? subtitle.trim() : null, tooltipTimeSpan, tooltipProgressStr, cancelTooltip]
        .filter(Boolean)
        .join(' • ');
  const isRecorded = session.is_recorded ?? false;
  const isFinalSession = session.is_final_session ?? false;
  const isExcessSession = session.is_excess_session ?? false;
  const podcastBadgeColor = getProjectTypeBadgeColor('Podcast');
  const splitRatio = session._split_ratio ?? 1;
  const nextProjectNames = session._next_project_names;
  const hasSplit = (isFinalSession || isExcessSession) && !isRecorded;

  /** Текст/разметка следующего проекта для split-зоны (при >1 проекте «и ещё N» жирным) */
  const nextProjectLabel: React.ReactNode = (() => {
    if (!hasSplit) return null;
    if (!nextProjectNames || nextProjectNames.length === 0) return <span className="session-bubble__split-no-project">Нет проекта</span>;
    if (nextProjectNames.length === 1) return nextProjectNames[0];
    return <>{nextProjectNames[0]} <strong>и ещё {nextProjectNames.length - 1}</strong></>;
  })();

  /** Без fallback на font_size: иначе смена основного шрифта тянет за собой зону «следующий проект». */
  const splitZoneFontSize = Math.max(0, Math.min(5, session.second_project_font_size ?? 3));

  const commentBadge = cellCommentShell?.commentBadge ?? null;

  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [submenuType, setSubmenuType] = useState<'quick' | 'display' | null>(null);
  const [submenuEl, setSubmenuEl] = useState<HTMLElement | null>(null);
  /** Якорь второго уровня: Быстрые настройки → Звукорежиссёр */
  const [engineerSubmenuEl, setEngineerSubmenuEl] = useState<HTMLElement | null>(null);
  const [projectPickSubmenuEl, setProjectPickSubmenuEl] = useState<HTMLElement | null>(null);
  const [readerPickSubmenuEl, setReaderPickSubmenuEl] = useState<HTMLElement | null>(null);
  const [fontSizeMenuAnchor, setFontSizeMenuAnchor] = useState<HTMLElement | null>(null);
  const [secondFontSizeMenuAnchor, setSecondFontSizeMenuAnchor] = useState<HTMLElement | null>(null);
  const [cancelReasonModalOpen, setCancelReasonModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);
  const submenuCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [resizing, setResizing] = useState<'top' | 'bottom' | null>(null);
  const [isDropTarget, setIsDropTarget] = useState(false);
  const resizeResultRef = useRef<{ newStart: number; newEnd: number } | null>(null);
  const bubbleRef = useRef<HTMLDivElement>(null);
  const contentHeaderHidden = session.is_header_hidden ?? false;
  const contentReaderHidden = session.is_reader_hidden ?? false;
  const contentProjectHidden = session.is_project_hidden ?? false;
  const bubbleReaderHidden = isPodcastSession ? contentProjectHidden : contentReaderHidden;
  const bubbleProjectHidden = isPodcastSession ? contentReaderHidden : contentProjectHidden;
  const contentFontLevel = Math.max(0, Math.min(5, session.font_size ?? 3));
  const setChatAttachDrag = useChatAttachDragSetter();
  const dragStartPendingRef = useRef(false);
  const splitRatioRef = useRef(splitRatio);
  splitRatioRef.current = splitRatio;

  /** Кеш цвета звукорежиссёра per-instance через ref.
   * При move/edit/resize session может временно прийти без soundengineer_color — используем последний известный.
   * Чтение/запись ref во время рендера допустимо: не вызывает ре-рендер, идемпотентно в Strict Mode */
  const lastEngineerColorRef = useRef<string | null>(session.soundengineer_color ?? null);
  if (session.soundengineer_color) {
    lastEngineerColorRef.current = session.soundengineer_color;
  } else if (!session.soundengineer_id) {
    lastEngineerColorRef.current = null;
  }
  const engineerColor = !isNote
    ? (session.soundengineer_color || (session.soundengineer_id ? lastEngineerColorRef.current : null))
    : null;
  const engineerStyle = engineerColor ? buildEngineerStyle(engineerColor) : null;

  const { data: projectPickData, isLoading: projectPickLoading, isError: projectPickError } = useQuery({
    queryKey: ['projects', 'sessionBubblePick', getPrimarySessionReaderId(session) ?? 'none'],
    queryFn: async () => {
      const res = await api.projects.list({
        readerId: getPrimarySessionReaderId(session)?.trim() || undefined,
        excludeReady: true,
        limit: SESSION_BUBBLE_PROJECT_PICK_LIMIT,
        offset: 0,
      });
      const items = Array.isArray(res) ? res : res.items;
      return items.filter((p) => getBaseStatus(p.state) !== 'Готов');
    },
    enabled: !!projectPickSubmenuEl,
    staleTime: 5_000,
  });
  const projectPickSections = useMemo(
    () => groupProjectsForPickerSections(projectPickData ?? [], getPrimarySessionReaderId(session)),
    [projectPickData, session]
  );

  const { data: readerPickProject, isLoading: readerListLoading, isError: readerListError } = useQuery({
    queryKey: ['project', session.project_id ?? ''],
    queryFn: () => api.projects.get(session.project_id!),
    enabled: !!readerPickSubmenuEl && !!session.project_id?.trim(),
    staleTime: 5_000,
  });
  const readerPickSections = useMemo(
    () => groupReadersForPickerSections(readerPickProject?.readers ?? []),
    [readerPickProject?.readers]
  );

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuPos({ x: e.clientX, y: e.clientY });
  }, []);

  const handleDragStart = useCallback((e: React.DragEvent) => {
    if (isRecorded && !hasCopyModifier(e)) {
      e.preventDefault();
      return;
    }
    setDraggingSession(session.id, isRecorded);
    const payload = {
      id: session.id,
      date: (session.date as string).slice(0, 10),
      project_id: session.project_id ?? undefined,
      label: `${(session.date as string).slice(0, 10)} ${getSessionReaderNames(session) || SESSION_READER_NOT_SET_LABEL} • ${session.project_name || '—'}`,
    };
    e.dataTransfer.setData(SESSION_DRAG_TYPE, session.id);
    e.dataTransfer.setData(SESSION_CHAT_ATTACH_TYPE, JSON.stringify(payload));
    e.dataTransfer.setData('text/plain', session.id);
    // Записанную сессию drop всё равно только копирует; 'copyMove' нужен для ⌘ на Mac — там система оставляет лишь перемещение
    e.dataTransfer.effectAllowed = 'copyMove';

    e.dataTransfer.setDragImage(getTransparentDragImage(), 0, 0);

    dragStartPendingRef.current = true;
    const el = bubbleRef.current;
    if (el && setChatAttachDrag) {
      const rect = el.getBoundingClientRect();
      const clone = el.cloneNode(true) as HTMLElement;
      Object.assign(clone.style, {
        position: 'relative',
        left: '0',
        top: '0',
        width: `${rect.width}px`,
        height: `${rect.height}px`,
        opacity: '1',
        boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
        pointerEvents: 'none',
      });
      clone.classList.add('session-bubble_drag-ghost');
      const ghostX = e.clientX;
      const ghostY = e.clientY;
      requestAnimationFrame(() => {
        if (!dragStartPendingRef.current) return;
        dragStartPendingRef.current = false;
        setChatAttachDrag({
          type: 'session',
          payload,
          ghostEl: clone,
          position: { x: ghostX, y: ghostY },
          positionMode: CHAT_ATTACH_DRAG_POSITION_MODE,
          positionOffsetY: CHAT_ATTACH_DRAG_OFFSET_Y_PX,
        });
        if (!isRecorded) {
          onDraggingChange?.(true);
          el?.classList.add('session-bubble_dragging');
        }
      });
    } else if (!isRecorded) {
      requestAnimationFrame(() => {
        if (!dragStartPendingRef.current) return;
        dragStartPendingRef.current = false;
        onDraggingChange?.(true);
        el?.classList.add('session-bubble_dragging');
      });
    }
  }, [session, isRecorded, setChatAttachDrag, onDraggingChange]);

  const handleDragEnd = useCallback(() => {
    dragStartPendingRef.current = false;
    const wasCommittedDrop = consumeSessionDropCommitted(session.id);
    clearDraggingSession();
    setChatAttachDrag?.(null);
    const revealSource = () => {
      onDraggingChange?.(false);
      bubbleRef.current?.classList.remove('session-bubble_dragging');
    };
    if (wasCommittedDrop) {
      afterNextPaint(revealSource);
      return;
    }
    revealSource();
  }, [session.id, setChatAttachDrag, onDraggingChange]);

  const handleResizeMouseDown = useCallback(
    (direction: 'top' | 'bottom') => (e: React.PointerEvent) => {
      /* Только ЛКМ: средняя кнопка зарезервирована под pan скролла (useMiddleButtonPanScroll). */
      if (e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      if (isRecorded) return;
      const startMins = parseTimeToMinutes(session.start as string);
      const endMins = parseTimeToMinutes(session.end as string);
      /** Пузырь absolute внутри оболочки, у оболочки уже top = этому значению; превью не в координатах всего дня. */
      const shellTopPx = (startMins / TOTAL_MINUTES) * CONTENT_HEIGHT;
      const startY = e.clientY;
      const pxPerMin = CONTENT_HEIGHT / TOTAL_MINUTES;
      const pointerId = e.pointerId;

      let resizeActive = true;
      let timePreviewRafId: number | null = null;
      let pendingTimePreview: { start: number; end: number } = { start: startMins, end: endMins };

      setResizing(direction);
      setResizeTimePreview({ start: startMins, end: endMins });
      (e.target as HTMLElement).setPointerCapture(pointerId);

      const handlePointerMove = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        const deltaY = ev.clientY - startY;
        const deltaMins = deltaY / pxPerMin;
        let newStart = startMins;
        let newEnd = endMins;
        if (direction === 'top') {
          const rawStart = Math.max(0, Math.min(startMins + deltaMins, endMins - 30));
          newStart = Math.round(rawStart / 30) * 30;
          newStart = Math.max(0, Math.min(newStart, endMins - 30));
          resizeResultRef.current = { newStart, newEnd: endMins };
        } else {
          const rawEnd = Math.min(TOTAL_MINUTES, Math.max(endMins + deltaMins, startMins + 30));
          newEnd = Math.round(rawEnd / 30) * 30;
          newEnd = Math.min(TOTAL_MINUTES, Math.max(newEnd, startMins + 30));
          resizeResultRef.current = { newStart: startMins, newEnd };
        }
        if (bubbleRef.current) {
          const newTopPx = (newStart / TOTAL_MINUTES) * CONTENT_HEIGHT;
          const newHeightPx = ((newEnd - newStart) / TOTAL_MINUTES) * CONTENT_HEIGHT;
          bubbleRef.current.style.top = `${newTopPx - shellTopPx}px`;
          bubbleRef.current.style.height = `${newHeightPx}px`;
        }
        pendingTimePreview = { start: newStart, end: newEnd };
        if (timePreviewRafId == null) {
          timePreviewRafId = requestAnimationFrame(() => {
            timePreviewRafId = null;
            if (!resizeActive) return;
            setResizeTimePreview(pendingTimePreview);
          });
        }
      };

      let finished = false;
      const finishResize = () => {
        if (finished) return;
        finished = true;
        resizeActive = false;
        if (timePreviewRafId != null) {
          cancelAnimationFrame(timePreviewRafId);
          timePreviewRafId = null;
        }
        document.removeEventListener('pointermove', handlePointerMove);
        document.removeEventListener('pointerup', handlePointerUp);
        document.removeEventListener('pointercancel', handlePointerUp);
        window.removeEventListener('blur', handleBlur);
        const result = resizeResultRef.current;
        if (result) {
          onSessionOptimisticUpdate(session.id, {
            start: buildTimeFromMinutes(result.newStart),
            end: buildTimeFromMinutes(result.newEnd),
          });
          onSessionResize(session, result.newStart, result.newEnd);
          afterNextPaint(() => {
            setResizing(null);
            setResizeTimePreview(null);
            if (bubbleRef.current) {
              bubbleRef.current.style.top = '0px';
              bubbleRef.current.style.height = '100%';
            }
          });
        } else {
          setResizing(null);
          setResizeTimePreview(null);
          if (bubbleRef.current) {
            bubbleRef.current.style.top = '0px';
            bubbleRef.current.style.height = '100%';
          }
        }
        resizeResultRef.current = null;
      };

      const handlePointerUp = (ev: PointerEvent) => {
        if (ev.pointerId !== pointerId) return;
        finishResize();
      };

      const handleBlur = () => finishResize();

      resizeResultRef.current = { newStart: startMins, newEnd: endMins };
      document.addEventListener('pointermove', handlePointerMove);
      document.addEventListener('pointerup', handlePointerUp, { once: true });
      document.addEventListener('pointercancel', handlePointerUp, { once: true });
      window.addEventListener('blur', handleBlur, { once: true });
    },
    [session, isRecorded, onSessionOptimisticUpdate, onSessionResize]
  );

  const handleClick = useCallback(() => {
    onSessionClick();
  }, [onSessionClick]);

  const handleDoubleClick = useCallback(() => {
    onSessionEdit(session);
  }, [onSessionEdit, session]);

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      const { sessionId: draggedId, isRecorded: draggingRecorded } = scheduleDragState;
      if (!draggedId || draggedId === session.id) return;
      if (!e.dataTransfer.types.includes(SESSION_DRAG_TYPE)) return;
      if (draggingRecorded) return;
      if (isRecorded) return;
      e.preventDefault();
      e.stopPropagation();
      const dropEffect = getSessionDropEffect(e, false);
      if (dropEffect) e.dataTransfer.dropEffect = dropEffect;
      setIsDropTarget(true);
    },
    [session.id, isRecorded]
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    const related = e.relatedTarget as Node | null;
    if (related && bubbleRef.current?.contains(related)) return;
    setIsDropTarget(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      setIsDropTarget(false);
      if (scheduleDragState.isRecorded) return;
      if (isRecorded) return;
      const draggedId = e.dataTransfer.getData(SESSION_DRAG_TYPE);
      if (!draggedId || draggedId === session.id) return;
      e.preventDefault();
      e.stopPropagation();
      onSessionDropOnSession(draggedId, session, hasCopyModifier(e));
    },
    [session, onSessionDropOnSession, isRecorded]
  );

  useEffect(() => {
    const clearDropTarget = () => setIsDropTarget(false);
    window.addEventListener('dragend', clearDropTarget);
    return () => window.removeEventListener('dragend', clearDropTarget);
  }, []);

  const clearSubmenuTimer = useCallback(() => {
    if (submenuCloseTimerRef.current) {
      clearTimeout(submenuCloseTimerRef.current);
      submenuCloseTimerRef.current = null;
    }
  }, []);

  const scheduleSubmenuClose = useCallback(() => {
    clearSubmenuTimer();
    submenuCloseTimerRef.current = setTimeout(() => {
      setSubmenuType(null);
      setSubmenuEl(null);
      setEngineerSubmenuEl(null);
      setProjectPickSubmenuEl(null);
      setReaderPickSubmenuEl(null);
      submenuCloseTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY_MS);
  }, [clearSubmenuTimer]);

  /** Третий уровень в портале — не сбрасывать весь каскад при уходе курсора с подпанели. */
  const scheduleThirdLevelClose = useCallback(() => {
    clearSubmenuTimer();
    submenuCloseTimerRef.current = setTimeout(() => {
      setEngineerSubmenuEl(null);
      setProjectPickSubmenuEl(null);
      setReaderPickSubmenuEl(null);
      submenuCloseTimerRef.current = null;
    }, SUBMENU_CLOSE_DELAY_MS);
  }, [clearSubmenuTimer]);

  /** Пока открыт третий уровень, mouseleave контейнера «Быстрые настройки» из‑за портала — не закрывать второй уровень. */
  const handleQuickSubmenuContainerMouseLeave = useCallback(() => {
    if (engineerSubmenuEl || projectPickSubmenuEl || readerPickSubmenuEl) return;
    scheduleSubmenuClose();
  }, [engineerSubmenuEl, projectPickSubmenuEl, readerPickSubmenuEl, scheduleSubmenuClose]);

  const closeContextMenu = useCallback(() => {
    clearSubmenuTimer();
    setMenuPos(null);
    setSubmenuType(null);
    setSubmenuEl(null);
    setEngineerSubmenuEl(null);
    setProjectPickSubmenuEl(null);
    setReaderPickSubmenuEl(null);
  }, [clearSubmenuTimer]);

  const handleSoundengineerSelect = useCallback(
    async (engineer: { id: string; name: string; color?: string | null }) => {
      closeContextMenu();
      try {
        await onSessionUpdate(session.id, {
          soundengineer_id: engineer.id,
          soundengineer_name: engineer.name,
          soundengineer_color: engineer.color ?? null,
        });
      } catch {
        // rollback in mutation onError
      }
    },
    [closeContextMenu, session.id, onSessionUpdate]
  );

  const handleClearSoundengineer = useCallback(async () => {
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, {
        soundengineer_id: null,
        soundengineer_name: null,
        soundengineer_color: null,
      });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session.id, onSessionUpdate]);

  const handleOpenFontSizeMenu = useCallback(() => {
    setFontSizeMenuAnchor(bubbleRef.current);
    closeContextMenu();
  }, [closeContextMenu]);

  const handleOpenSecondFontSizeMenu = useCallback(() => {
    setSecondFontSizeMenuAnchor(bubbleRef.current);
    closeContextMenu();
  }, [closeContextMenu]);

  const handleToggleReaderVisibility = useCallback(async () => {
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, {
        is_reader_hidden: !(session.is_reader_hidden ?? false),
      });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session.id, session.is_reader_hidden, onSessionUpdate]);

  const handleToggleProjectVisibility = useCallback(async () => {
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, {
        is_project_hidden: !(session.is_project_hidden ?? false),
      });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session.id, session.is_project_hidden, onSessionUpdate]);

  const handleToggleHeaderVisibility = useCallback(async () => {
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, {
        is_header_hidden: !(session.is_header_hidden ?? false),
      });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session.id, session.is_header_hidden, onSessionUpdate]);

  const handleToggleProgressVisibility = useCallback(async () => {
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, {
        is_progress_hidden: !(session.is_progress_hidden ?? false),
      });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session.id, session.is_progress_hidden, onSessionUpdate]);

  const handleClearProject = useCallback(async () => {
    if (!session.project_id) return;
    if (!window.confirm('Убрать проект у этой сессии? Сессия останется в расписании без привязки к проекту.')) return;
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, {
        project_id: null,
        project_name: null,
        project_type: null,
      });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session.id, session.project_id, onSessionUpdate]);

  const handleClearReader = useCallback(async () => {
    if (!getPrimarySessionReaderId(session)) return;
    if (!window.confirm('Убрать чтеца у этой сессии?')) return;
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, { reader_ids: [], reader_id: null, reader_name: null, readers: [] });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session, onSessionUpdate]);

  const handlePickProject = useCallback(
    async (project: Project) => {
      closeContextMenu();
      try {
        const readers = project.readers ?? [];
        const rid = getPrimarySessionReaderId(session);
        const readerStillOk = !!(rid && readers.some((r) => r.id === rid));
        const patch: Parameters<typeof onSessionUpdate>[1] = {
          project_id: project.id,
          project_name: project.name,
          project_type: project.project_type ?? null,
        };
        if (project.project_type === 'Podcast' && session.session_type === 'запись') {
          patch.session_subtype = 'podcast';
          patch.reader_ids = readers.map((r) => r.id);
          patch.readers = readers.map((r) => ({ id: r.id, name: r.name }));
          await onSessionUpdate(session.id, patch);
          return;
        }
        patch.session_subtype = null;
        if (!readerStillOk) {
          patch.reader_ids = [];
          patch.reader_name = null;
        } else if (readers.length === 1 && !rid) {
          const only = readers[0];
          patch.reader_ids = [only.id];
          patch.reader_name = only.name;
        }
        await onSessionUpdate(session.id, patch);
      } catch {
        // rollback in mutation onError
      }
    },
    [closeContextMenu, session, onSessionUpdate]
  );

  const handlePickReader = useCallback(
    async (reader: { id: string; name: string }) => {
      closeContextMenu();
      try {
        await onSessionUpdate(session.id, { reader_ids: [reader.id], reader_id: reader.id, reader_name: reader.name });
      } catch {
        // rollback in mutation onError
      }
    },
    [closeContextMenu, session.id, onSessionUpdate]
  );

  const handleDeleteClick = useCallback(() => {
    closeContextMenu();
    onSessionDelete(session.id);
  }, [closeContextMenu, session.id, onSessionDelete]);

  const handleOpenCancelSessionModal = useCallback(() => {
    closeContextMenu();
    setCancelReasonModalOpen(true);
  }, [closeContextMenu]);

  const handleSaveCancelReason = useCallback(
    async (note: string) => {
      await onSessionUpdate(session.id, {
        is_canceled: true,
        canceled_note: note.length ? note.slice(0, 300) : null,
      });
    },
    [session.id, onSessionUpdate]
  );

  const handleRestoreSession = useCallback(async () => {
    closeContextMenu();
    try {
      await onRestoreCanceledSession(session);
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, onRestoreCanceledSession, session]);

  const handleHideCanceledFromSchedule = useCallback(async () => {
    closeContextMenu();
    try {
      await onSessionUpdate(session.id, { canceled_hidden_from_schedule: true });
    } catch {
      // rollback in mutation onError
    }
  }, [closeContextMenu, session.id, onSessionUpdate]);

  const sharedDropProps = {
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  } as const;

  const contextMenuAndFontSize = (
    <>
      <ContextMenu
        isOpen={!!menuPos}
        onClose={closeContextMenu}
        anchorPosition={menuPos}
      >
        <ul className="context-menu__list">
          {isCanceled && !(session.canceled_hidden_from_schedule ?? false) ? (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__action context-menu__action_danger"
                onClick={handleHideCanceledFromSchedule}
              >
                <VisibilityOffIcon size={18} />
                <span className="context-menu__label-text">Скрыть сессию</span>
              </button>
            </li>
          ) : null}
          {teleport && session.project_id && (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__action"
                onClick={() => {
                  teleport.goToProject(session.project_id!);
                  closeContextMenu();
                }}
              >
                <ArrowForwardIcon size={18} />
                <span className="context-menu__label-text">Перейти к проекту</span>
              </button>
            </li>
          )}
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action"
              onClick={() => {
                onSessionEdit(session);
                closeContextMenu();
              }}
            >
              <EditIcon size={18} />
              <span className="context-menu__label-text">Редактировать</span>
            </button>
          </li>
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action"
              onClick={() => {
                setHistoryAnchorEl(bubbleRef.current);
                closeContextMenu();
                setLastUndoRedoSessionId(session.id);
                setHistoryOpen(true);
              }}
            >
              <HistoryIcon size={18} />
              <span className="context-menu__label-text">Посмотреть изменения</span>
            </button>
          </li>
          {cellCommentShell ? (
            <li className="context-menu__list-item">
              <button
                type="button"
                className="context-menu__action"
                onClick={() => {
                  cellCommentShell.addCommentMenuItem.onClick();
                  closeContextMenu();
                }}
              >
                {cellCommentShell.addCommentMenuItem.icon}
                <span className="context-menu__label-text">
                  {cellCommentShell.addCommentMenuItem.label}
                </span>
              </button>
            </li>
          ) : null}
          <li
            className="context-menu__list-item context-menu__list-item_with-sub"
            onMouseEnter={(e) => {
              clearSubmenuTimer();
              setSubmenuType('display');
              setSubmenuEl(e.currentTarget);
              setEngineerSubmenuEl(null);
              setProjectPickSubmenuEl(null);
              setReaderPickSubmenuEl(null);
            }}
            onMouseLeave={scheduleSubmenuClose}
          >
            <button type="button" className="context-menu__action context-menu__action_with-sub">
              <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
              <span className="context-menu__label-text">Настройки отображения</span>
              <ChevronRightIcon size={16} className="context-menu__action-chevron" />
            </button>
            {submenuType === 'display' && submenuEl && (
              <ContextMenuSubmenu
                anchorEl={submenuEl}
                onMouseEnter={clearSubmenuTimer}
                onMouseLeave={() => {
                  setSubmenuType(null);
                  setSubmenuEl(null);
                }}
              >
                <ul className="context-menu__list">
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={splitRatio === 0 ? handleOpenSecondFontSizeMenu : handleOpenFontSizeMenu}
                        >
                          <TextDecreaseIcon size={18} />
                          <span className="context-menu__label-text">Размер шрифта</span>
                        </button>
                      </li>
                      {hasSplit && splitRatio > 0 && (
                        <li className="context-menu__list-item">
                          <button
                            type="button"
                            className="context-menu__action"
                            onClick={handleOpenSecondFontSizeMenu}
                          >
                            <TextDecreaseIcon size={18} />
                            <span className="context-menu__label-text">Размер шрифта следующего проекта</span>
                          </button>
                        </li>
                      )}
                  {session.session_type === 'заметка' && (
                    <li className="context-menu__list-item">
                      <button
                        type="button"
                        className="context-menu__action"
                        onClick={handleToggleHeaderVisibility}
                      >
                        {(session.is_header_hidden ?? false) ? (
                          <>
                            <VisibilityIcon size={18} />
                            <span className="context-menu__label-text">Показать заголовок</span>
                          </>
                        ) : (
                          <>
                            <VisibilityOffIcon size={18} />
                            <span className="context-menu__label-text">Скрыть заголовок</span>
                          </>
                        )}
                      </button>
                    </li>
                  )}
                  {(session.session_type === 'запись' || session.session_type === 'правки') && (
                    <>
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleToggleReaderVisibility}
                        >
                          {(session.is_reader_hidden ?? false) ? (
                            <>
                              <PersonIcon size={18} />
                              <span className="context-menu__label-text">Показать имя чтеца</span>
                            </>
                          ) : (
                            <>
                              <PersonOffIcon size={18} />
                              <span className="context-menu__label-text">Скрыть имя чтеца</span>
                            </>
                          )}
                        </button>
                      </li>
                      <li className="context-menu__list-item">
                        <button
                          type="button"
                          className="context-menu__action"
                          onClick={handleToggleProjectVisibility}
                        >
                          {(session.is_project_hidden ?? false) ? (
                            <>
                              <SubtitlesIcon sx={{ fontSize: 18 }} />
                              <span className="context-menu__label-text">Показать название проекта</span>
                            </>
                          ) : (
                            <>
                              <SubtitlesOffIcon sx={{ fontSize: 18 }} />
                              <span className="context-menu__label-text">Скрыть название проекта</span>
                            </>
                          )}
                        </button>
                      </li>
                      {(session.session_type ?? 'запись') === 'запись' && !isPodcastSession && (
                        <li className="context-menu__list-item">
                          <button
                            type="button"
                            className="context-menu__action"
                            onClick={handleToggleProgressVisibility}
                          >
                            {(session.is_progress_hidden ?? false) ? (
                              <>
                                <CreditCardIcon sx={{ fontSize: 18 }} />
                                <span className="context-menu__label-text">Показать прогресс</span>
                              </>
                            ) : (
                              <>
                                <CreditCardOffIcon sx={{ fontSize: 18 }} />
                                <span className="context-menu__label-text">Скрыть прогресс</span>
                              </>
                            )}
                          </button>
                        </li>
                      )}
                    </>
                  )}
                </ul>
              </ContextMenuSubmenu>
            )}
          </li>
          {!isNote && (
            <li
              className="context-menu__list-item context-menu__list-item_with-sub"
              onMouseEnter={(e) => {
                clearSubmenuTimer();
                setSubmenuType('quick');
                setSubmenuEl(e.currentTarget);
                setEngineerSubmenuEl(null);
                setProjectPickSubmenuEl(null);
                setReaderPickSubmenuEl(null);
              }}
              onMouseLeave={scheduleSubmenuClose}
            >
              <button type="button" className="context-menu__action context-menu__action_with-sub">
                <TuneIcon size={18} />
                <span className="context-menu__label-text">Быстрые настройки</span>
                <ChevronRightIcon size={16} className="context-menu__action-chevron" />
              </button>
              {submenuType === 'quick' && submenuEl && (
                <ContextMenuSubmenu
                  anchorEl={submenuEl}
                  onMouseEnter={clearSubmenuTimer}
                  onMouseLeave={handleQuickSubmenuContainerMouseLeave}
                >
                  <ul className="context-menu__list">
                    <li
                      className="context-menu__list-item context-menu__list-item_with-sub"
                      onMouseEnter={(e) => {
                        clearSubmenuTimer();
                        setEngineerSubmenuEl(e.currentTarget);
                        setProjectPickSubmenuEl(null);
                        setReaderPickSubmenuEl(null);
                      }}
                      onMouseLeave={scheduleThirdLevelClose}
                    >
                      <button type="button" className="context-menu__action context-menu__action_with-sub">
                        <HeadsetMicIcon size={18} />
                        <span className="context-menu__label-text">Звукорежиссёр</span>
                        <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                      </button>
                      {engineerSubmenuEl && (
                        <ContextMenuSubmenu
                          anchorEl={engineerSubmenuEl}
                          onMouseEnter={clearSubmenuTimer}
                          onMouseLeave={scheduleThirdLevelClose}
                          zIndex={1150}
                        >
                          <ul className="context-menu__list">
                            <li className="context-menu__list-item">
                              <button
                                type="button"
                                className={`context-menu__action ${!session.soundengineer_id ? 'context-menu__action_selected' : ''}`}
                                onClick={() => handleClearSoundengineer()}
                              >
                                <span className="session-bubble__engineer-dot session-bubble__engineer-dot_empty" aria-hidden />
                                <span className="context-menu__label-text">Не назначен</span>
                              </button>
                            </li>
                            {soundengineers.map((eng) => (
                              <li key={eng.id} className="context-menu__list-item">
                                <button
                                  type="button"
                                  className={`context-menu__action ${session.soundengineer_id === eng.id ? 'context-menu__action_selected' : ''}`}
                                  onClick={() => handleSoundengineerSelect(eng)}
                                >
                                  <span
                                    className="session-bubble__engineer-dot"
                                    style={{ backgroundColor: eng.color || DEFAULT_ENGINEER_COLOR }}
                                    aria-hidden
                                  />
                                  <span className="context-menu__label-text">{eng.name}</span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </ContextMenuSubmenu>
                      )}
                    </li>
                    <li
                      className="context-menu__list-item context-menu__list-item_with-sub"
                      onMouseEnter={(e) => {
                        clearSubmenuTimer();
                        setProjectPickSubmenuEl(e.currentTarget);
                        setEngineerSubmenuEl(null);
                        setReaderPickSubmenuEl(null);
                      }}
                      onMouseLeave={scheduleThirdLevelClose}
                    >
                      <button type="button" className="context-menu__action context-menu__action_with-sub">
                        <SubtitlesIcon sx={{ fontSize: 18 }} />
                        <span className="context-menu__label-text">Проект</span>
                        <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                      </button>
                      {projectPickSubmenuEl && (
                        <ContextMenuSubmenu
                          anchorEl={projectPickSubmenuEl}
                          onMouseEnter={clearSubmenuTimer}
                          onMouseLeave={scheduleThirdLevelClose}
                          zIndex={1150}
                        >
                          <ul className="context-menu__list">
                            <li className="context-menu__list-item">
                              <button
                                type="button"
                                className="context-menu__action context-menu__action_danger"
                                disabled={!session.project_id}
                                onClick={() => void handleClearProject()}
                              >
                                <SubtitlesOffIcon sx={{ fontSize: 18 }} />
                                <span className="context-menu__label-text">Очистить проект</span>
                              </button>
                            </li>
                            {projectPickLoading && (
                              <li className="context-menu__list-item" aria-busy>
                                <div className="session-bubble__pick-loading" role="status">
                                  <span className="session-bubble__pick-spinner" aria-hidden />
                                  <span className="session-bubble__pick-loading-text">Загрузка…</span>
                                </div>
                              </li>
                            )}
                            {!projectPickLoading && projectPickError && (
                              <li className="context-menu__list-item">
                                <div className="session-bubble__pick-empty">Не удалось загрузить проекты</div>
                              </li>
                            )}
                            {!projectPickLoading &&
                              !projectPickError &&
                              projectPickSections.flatMap((s) => s.projects).length === 0 && (
                              <li className="context-menu__list-item">
                                <div className="session-bubble__pick-empty">Нет проектов (кроме «Готов»)</div>
                              </li>
                            )}
                            {!projectPickLoading &&
                              !projectPickError &&
                              projectPickSections.map((sec) => (
                                <Fragment key={sec.label}>
                                  <ContextMenuSectionHeader label={sec.label} />
                                  {sec.projects.map((p) => {
                                    const selected = session.project_id === p.id;
                                    return (
                                      <li key={p.id} className="context-menu__list-item">
                                        <button
                                          type="button"
                                          className={`context-menu__action ${selected ? 'context-menu__action_selected' : ''}`}
                                          onClick={() => void handlePickProject(p)}
                                        >
                                          <span className="session-bubble__pick-option-inner overlay-picker-badge-row">
                                            <Badge variant="status" status={p.state} />
                                            <DeadlineRemainingBadge deadline={p.deadline} projectState={p.state} />
                                            <span className="session-bubble__pick-name">{p.name}</span>
                                          </span>
                                        </button>
                                      </li>
                                    );
                                  })}
                                </Fragment>
                              ))}
                          </ul>
                        </ContextMenuSubmenu>
                      )}
                    </li>
                    {(session.session_type === 'запись' || session.session_type === 'правки') && (
                      <li
                        className="context-menu__list-item context-menu__list-item_with-sub"
                        onMouseEnter={(e) => {
                          clearSubmenuTimer();
                          setReaderPickSubmenuEl(e.currentTarget);
                          setEngineerSubmenuEl(null);
                          setProjectPickSubmenuEl(null);
                        }}
                        onMouseLeave={scheduleThirdLevelClose}
                      >
                        <button type="button" className="context-menu__action context-menu__action_with-sub">
                          <PersonIcon size={18} />
                          <span className="context-menu__label-text">Чтец</span>
                          <ChevronRightIcon size={16} className="context-menu__action-chevron" />
                        </button>
                        {readerPickSubmenuEl && (
                          <ContextMenuSubmenu
                            anchorEl={readerPickSubmenuEl}
                            onMouseEnter={clearSubmenuTimer}
                            onMouseLeave={scheduleThirdLevelClose}
                            zIndex={1150}
                          >
                            <ul className="context-menu__list">
                              <li className="context-menu__list-item">
                                <button
                                  type="button"
                                  className="context-menu__action context-menu__action_danger"
                                  disabled={!getPrimarySessionReaderId(session)}
                                  onClick={() => void handleClearReader()}
                                >
                                  <PersonOffIcon size={18} />
                                  <span className="context-menu__label-text">Очистить чтеца</span>
                                </button>
                              </li>
                              {!session.project_id?.trim() && (
                                <li className="context-menu__list-item">
                                  <div className="session-bubble__pick-empty">Сначала укажите проект у сессии</div>
                                </li>
                              )}
                              {!!session.project_id?.trim() && readerListLoading && (
                                <li className="context-menu__list-item" aria-busy>
                                  <div className="session-bubble__pick-loading" role="status">
                                    <span className="session-bubble__pick-spinner" aria-hidden />
                                    <span className="session-bubble__pick-loading-text">Загрузка…</span>
                                  </div>
                                </li>
                              )}
                              {!!session.project_id?.trim() && !readerListLoading && readerListError && (
                                <li className="context-menu__list-item">
                                  <div className="session-bubble__pick-empty">Не удалось загрузить чтецов</div>
                                </li>
                              )}
                              {!!session.project_id?.trim() &&
                                !readerListLoading &&
                                !readerListError &&
                                readerPickSections.flatMap((s) => s.readers).length === 0 && (
                                  <li className="context-menu__list-item">
                                    <div className="session-bubble__pick-empty">В проекте нет чтецов</div>
                                  </li>
                                )}
                              {!!session.project_id?.trim() &&
                                !readerListLoading &&
                                !readerListError &&
                                readerPickSections.map((sec) => (
                                  <Fragment key={sec.label}>
                                    <ContextMenuSectionHeader label={sec.label} />
                                    {sec.readers.map((r) => {
                                      const selected = getPrimarySessionReaderId(session) === r.id;
                                      return (
                                        <li key={r.id} className="context-menu__list-item">
                                          <button
                                            type="button"
                                            className={`context-menu__action ${selected ? 'context-menu__action_selected' : ''}`}
                                            onClick={() => void handlePickReader(r)}
                                          >
                                            <span className="context-menu__label-text session-bubble__pick-name">{r.name}</span>
                                          </button>
                                        </li>
                                      );
                                    })}
                                  </Fragment>
                                ))}
                            </ul>
                          </ContextMenuSubmenu>
                        )}
                      </li>
                    )}
                  </ul>
                </ContextMenuSubmenu>
              )}
            </li>
          )}
          {(session.session_type === 'запись' || session.session_type === 'правки') && (
            <li className="context-menu__list-item">
              {isCanceled ? (
                <button type="button" className="context-menu__action" onClick={handleRestoreSession}>
                  <EventAvailableIcon sx={{ fontSize: 18 }} />
                  <span className="context-menu__label-text">Восстановить сессию</span>
                </button>
              ) : (
                <button
                  type="button"
                  className="context-menu__action context-menu__action_danger"
                  onClick={handleOpenCancelSessionModal}
                >
                  <EventBusyIcon sx={{ fontSize: 18 }} />
                  <span className="context-menu__label-text">Отменить сессию</span>
                </button>
              )}
            </li>
          )}
          <li className="context-menu__list-item">
            <button
              type="button"
              className="context-menu__action context-menu__action_danger"
              onClick={handleDeleteClick}
            >
              <DeleteIcon size={18} />
              <span className="context-menu__label-text">Удалить</span>
            </button>
          </li>
        </ul>
      </ContextMenu>
      <SessionChangeHistoryPopover
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setHistoryAnchorEl(null);
        }}
        anchorEl={historyAnchorEl}
        anchorPlacement="beside-menu"
        sessionId={session.id}
      />
      <FontSizeSelector
        session={session}
        anchorEl={fontSizeMenuAnchor}
        open={!!fontSizeMenuAnchor}
        onClose={() => setFontSizeMenuAnchor(null)}
        onSessionUpdate={onSessionUpdate}
        onSessionOptimisticUpdate={onSessionOptimisticUpdate}
        autoClose
      />
      <FontSizeSelector
        session={session}
        anchorEl={secondFontSizeMenuAnchor}
        open={!!secondFontSizeMenuAnchor}
        onClose={() => setSecondFontSizeMenuAnchor(null)}
        onSessionUpdate={onSessionUpdate}
        onSessionOptimisticUpdate={onSessionOptimisticUpdate}
        autoClose
        field="second_project_font_size"
      />
      <SessionCancelReasonModal
        open={cancelReasonModalOpen}
        onClose={() => setCancelReasonModalOpen(false)}
        initialNote={session.canceled_note ?? ''}
        onSave={handleSaveCancelReason}
      />
    </>
  );

  /** Финальная сессия (splitRatio > 0) или избыточная (splitRatio = 0):
   *  единый блок с единым бордером. Нижняя часть — overlay с полосками. */
  /** Dim только контент (не бейджи): opacity на корне не даёт детям стать визуально непрозрачными */
  const useRecordedContentDim = isRecorded && !isCanceled;
  const scheduleTypeBadges = (
    <Fragment>
      {isPodcastSession && (
        <span
          className="session-bubble__type-badge session-bubble__type-badge_podcast"
          style={{ background: podcastBadgeColor.bg, color: podcastBadgeColor.color }}
          aria-hidden
        >
          <span>{hlSch('подкаст')}</span>
          <span className="session-bubble__type-badge-ext">{hlSch(tooltipTimeSpan)}</span>
        </span>
      )}
      {(session.session_type ?? 'запись') === 'запись' && !isPodcastSession && (
        <span className="session-bubble__time" aria-hidden>
          {hlSch(`запись ${tooltipTimeSpan}`)}
        </span>
      )}
      {(session.session_type === 'правки' || session.session_type === 'монтаж' || session.session_type === 'заметка') && (
        <span
          className={`session-bubble__type-badge session-bubble__type-badge_${session.session_type}`}
          aria-hidden
        >
          <span>{hlSch(String(session.session_type ?? ''))}</span>
          <span className="session-bubble__type-badge-ext">{hlSch(tooltipTimeSpan)}</span>
        </span>
      )}
    </Fragment>
  );
  if (hasSplit) {
    const isTrueSplit = splitRatio > 0 && splitRatio < 1;
    return (
      <>
        <div
          ref={bubbleRef}
          className={`session-bubble ${isNote ? 'session-bubble_note' : ''} ${isRecorded ? 'session-bubble_recorded' : ''} ${isCanceled ? 'session-bubble_canceled' : ''} ${resizing ? `session-bubble_resizing session-bubble_resizing-${resizing}` : ''} ${isDropTarget ? 'session-bubble_drop-target' : ''} ${engineerColor && !isCanceled ? 'session-bubble_has-engineer' : ''} ${splitRatio === 0 ? 'session-bubble_excess' : ''}`}
          style={{
            top: 0,
            left: 'var(--session-visual-inset, 0px)',
            width: 'calc(100% - var(--session-visual-inset, 0px) - var(--session-visual-inset, 0px))',
            height: '100%',
            minHeight: 20,
            ...(engineerStyle && !isCanceled ? engineerStyle : {}),
          }}
          title={tooltipTitle}
          draggable
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onClick={handleClick}
          onContextMenu={handleContextMenu}
          onDoubleClick={handleDoubleClick}
          data-session-id={session.id}
          {...sharedDropProps}
        >
          {!isRecorded && (
            <>
              <div
                className={`session-bubble__resize-handle session-bubble__resize-handle_top ${resizing === 'top' ? 'session-bubble__resize-handle_active' : ''}`}
                onPointerDown={handleResizeMouseDown('top')}
              />
              <div
                className={`session-bubble__resize-handle session-bubble__resize-handle_bottom ${resizing === 'bottom' ? 'session-bubble__resize-handle_active' : ''}`}
                onPointerDown={handleResizeMouseDown('bottom')}
              />
            </>
          )}
          {scheduleTypeBadges}
          {commentBadge ? (
            <CellCommentCornerBadge
              onOpen={commentBadge.onOpen}
              onPointerEnter={commentBadge.onPointerEnter}
              onPointerLeave={commentBadge.onPointerLeave}
            />
          ) : null}
          {isRecorded && !isCanceled && (
            <span className="session-bubble__status session-bubble__status_recorded" title="Сессия записана">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
          )}
          {isCanceled && (
            <span className="session-bubble__status session-bubble__status_canceled" title="Запись отменена">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </span>
          )}
          {useRecordedContentDim ? (
            <div className="session-bubble__dimmable">
              {isTrueSplit && (
                <div className="session-bubble__main-part" style={{ bottom: `${(1 - splitRatio) * 100}%` }}>
                  <SessionBubbleContent
                    sessionType={session.session_type}
                    header={headerDisplay}
                    headerMuted={headerReaderMuted}
                    subtitle={subtitleDisplay}
                    hasStatus={isRecorded || isCanceled}
                    isCanceled={isCanceled}
                    fontSize={contentFontLevel}
                    isHeaderHidden={contentHeaderHidden}
                    isReaderHidden={bubbleReaderHidden}
                    isProjectHidden={bubbleProjectHidden}
                  />
                </div>
              )}
              <div
                className={`session-bubble__split-zone${splitRatio === 0 ? ' session-bubble__split-zone_full' : ''}`}
                style={{ top: `${splitRatio * 100}%` }}
              >
                {splitRatio === 0 ? (
                  <SessionBubbleContent
                    sessionType={session.session_type}
                    header={headerOrDash}
                    headerMuted={headerReaderMuted}
                    subtitle={excessProjectDisplay}
                    nextLine={nextProjectLabel != null ? <><strong>Далее: </strong>{nextProjectLabel}</> : undefined}
                    hasStatus={false}
                    isCanceled={isCanceled}
                    fontSize={contentFontLevel}
                    nextLineFontSize={splitZoneFontSize}
                    isHeaderHidden={false}
                    isReaderHidden={false}
                    isProjectHidden={bubbleProjectHidden}
                  />
                ) : (
                  <span
                    className="session-bubble__split-text"
                    style={{ fontSize: getContentFontSize(splitZoneFontSize) }}
                  >
                    <>
                      <strong>Далее: </strong>
                      {nextProjectLabel}
                    </>
                  </span>
                )}
              </div>
            </div>
          ) : (
            <>
              {isTrueSplit && (
                <div className="session-bubble__main-part" style={{ bottom: `${(1 - splitRatio) * 100}%` }}>
                  <SessionBubbleContent
                    sessionType={session.session_type}
                    header={headerDisplay}
                    headerMuted={headerReaderMuted}
                    subtitle={subtitleDisplay}
                    hasStatus={isRecorded || isCanceled}
                    isCanceled={isCanceled}
                    fontSize={contentFontLevel}
                    isHeaderHidden={contentHeaderHidden}
                    isReaderHidden={bubbleReaderHidden}
                    isProjectHidden={bubbleProjectHidden}
                  />
                </div>
              )}
              <div
                className={`session-bubble__split-zone${splitRatio === 0 ? ' session-bubble__split-zone_full' : ''}`}
                style={{ top: `${splitRatio * 100}%` }}
              >
                {splitRatio === 0 ? (
                  <SessionBubbleContent
                    sessionType={session.session_type}
                    header={headerOrDash}
                    headerMuted={headerReaderMuted}
                    subtitle={excessProjectDisplay}
                    nextLine={nextProjectLabel != null ? <><strong>Далее: </strong>{nextProjectLabel}</> : undefined}
                    hasStatus={false}
                    isCanceled={isCanceled}
                    fontSize={contentFontLevel}
                    nextLineFontSize={splitZoneFontSize}
                    isHeaderHidden={false}
                    isReaderHidden={false}
                    isProjectHidden={bubbleProjectHidden}
                  />
                ) : (
                  <span
                    className="session-bubble__split-text"
                    style={{ fontSize: getContentFontSize(splitZoneFontSize) }}
                  >
                    <>
                      <strong>Далее: </strong>
                      {nextProjectLabel}
                    </>
                  </span>
                )}
              </div>
            </>
          )}
        </div>
        {contextMenuAndFontSize}
      </>
    );
  }

  return (
    <>
      <div
        ref={bubbleRef}
        className={`session-bubble ${isNote ? 'session-bubble_note' : ''} ${isRecorded ? 'session-bubble_recorded' : ''} ${isCanceled ? 'session-bubble_canceled' : ''} ${resizing ? `session-bubble_resizing session-bubble_resizing-${resizing}` : ''} ${isDropTarget ? 'session-bubble_drop-target' : ''} ${engineerColor && !isCanceled ? 'session-bubble_has-engineer' : ''}`}
        style={{
          top: 0,
          left: 'var(--session-visual-inset, 0px)',
          width: 'calc(100% - var(--session-visual-inset, 0px) - var(--session-visual-inset, 0px))',
          height: '100%',
          minHeight: 20,
          ...(engineerStyle && !isCanceled ? engineerStyle : {}),
        }}
        title={tooltipTitle}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onDoubleClick={handleDoubleClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        data-session-id={session.id}
      >
        {!isRecorded && (
          <>
            <div
              className={`session-bubble__resize-handle session-bubble__resize-handle_top ${resizing === 'top' ? 'session-bubble__resize-handle_active' : ''}`}
              onPointerDown={handleResizeMouseDown('top')}
            />
            <div
              className={`session-bubble__resize-handle session-bubble__resize-handle_bottom ${resizing === 'bottom' ? 'session-bubble__resize-handle_active' : ''}`}
              onPointerDown={handleResizeMouseDown('bottom')}
            />
          </>
        )}
        {scheduleTypeBadges}
        {commentBadge ? (
          <CellCommentCornerBadge
            onOpen={commentBadge.onOpen}
            onPointerEnter={commentBadge.onPointerEnter}
            onPointerLeave={commentBadge.onPointerLeave}
          />
        ) : null}
        {isRecorded && !isCanceled && (
          <span className="session-bubble__status session-bubble__status_recorded" title="Сессия записана">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
        )}
        {isCanceled && (
          <span className="session-bubble__status session-bubble__status_canceled" title="Запись отменена">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </span>
        )}
        {useRecordedContentDim ? (
          <div className="session-bubble__dimmable">
            <SessionBubbleContent
              sessionType={session.session_type}
              header={headerDisplay}
              headerMuted={headerReaderMuted}
              subtitle={subtitleDisplay}
              hasStatus={isRecorded || isCanceled}
              isCanceled={isCanceled}
              fontSize={contentFontLevel}
              isHeaderHidden={contentHeaderHidden}
              isReaderHidden={bubbleReaderHidden}
              isProjectHidden={bubbleProjectHidden}
            />
            {(session.session_type ?? 'запись') === 'запись' &&
            !isPodcastSession &&
            (parseTimeToMinutes(session.end as string) - parseTimeToMinutes(session.start as string)) >= 60 &&
            !(session.is_progress_hidden ?? false) &&
            !isCanceled && (
            <div className="session-bubble__footer">
              <div className="session-bubble__progress-row">
                <div
                  className="session-bubble__progress-bar"
                  role="progressbar"
                  aria-valuenow={Math.min(100, Math.max(0, getPrimarySessionReaderId(session) ? (session.reader_progress ?? 0) : 0))}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="session-bubble__progress-bar-fill"
                    style={{ width: `${Math.min(100, Math.max(0, getPrimarySessionReaderId(session) ? (session.reader_progress ?? 0) : 0))}%` }}
                  />
                </div>
                <span className="session-bubble__progress-pct">
                  {Math.min(100, Math.max(0, getPrimarySessionReaderId(session) ? (session.reader_progress ?? 0) : 0))}%
                </span>
              </div>
            </div>
            )}
          </div>
        ) : (
          <>
            <SessionBubbleContent
              sessionType={session.session_type}
              header={headerDisplay}
              headerMuted={headerReaderMuted}
              subtitle={subtitleDisplay}
              hasStatus={isRecorded || isCanceled}
              isCanceled={isCanceled}
              fontSize={contentFontLevel}
              isHeaderHidden={contentHeaderHidden}
              isReaderHidden={bubbleReaderHidden}
              isProjectHidden={bubbleProjectHidden}
            />
            {(session.session_type ?? 'запись') === 'запись' &&
            !isPodcastSession &&
            (parseTimeToMinutes(session.end as string) - parseTimeToMinutes(session.start as string)) >= 60 &&
            !(session.is_progress_hidden ?? false) &&
            !isCanceled && (
            <div className="session-bubble__footer">
              <div className="session-bubble__progress-row">
                <div
                  className="session-bubble__progress-bar"
                  role="progressbar"
                  aria-valuenow={Math.min(100, Math.max(0, getPrimarySessionReaderId(session) ? (session.reader_progress ?? 0) : 0))}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div
                    className="session-bubble__progress-bar-fill"
                    style={{ width: `${Math.min(100, Math.max(0, getPrimarySessionReaderId(session) ? (session.reader_progress ?? 0) : 0))}%` }}
                  />
                </div>
                <span className="session-bubble__progress-pct">
                  {Math.min(100, Math.max(0, getPrimarySessionReaderId(session) ? (session.reader_progress ?? 0) : 0))}%
                </span>
              </div>
            </div>
            )}
          </>
        )}
      </div>
      {contextMenuAndFontSize}
    </>
  );
}

export const SessionBubble = memo(SessionBubbleRoot);
